import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { FilterComponent } from '@app/shared/components/filter/filter.component';
import { PaginationMeta } from '@app/shared/interfaces';
import { Filter } from '@app/shared/models/filter';
import { FilterService } from '@app/shared/services/filter/filter.service';
import { TeamService } from '@app/shared/services/team/team.service';
import { TitleService } from '@app/shared/services/title/title.service';

@Component({
  standalone: false,
  selector: 'sbnb-team',
  templateUrl: './team.component.html',
  styleUrls: ['./team.component.scss'],
})
export class TeamComponent implements OnInit {
  paginationData: PaginationMeta;
  loading: boolean;
  currentOffset: number;
  team: any;
  activeFilters: Filter[];
  searchCriteria: string;
  possibleRoles: any[];
  segmentFilterDefaults: any;

  @ViewChild('filterComponent')
  filterComponent: FilterComponent;

  constructor(
    private router: Router,
    private teamService: TeamService,
    private route: ActivatedRoute,
    private location: Location,
    private titleService: TitleService,
    private dialog: MatDialog,
    private filter: FilterService
  ) {}

  ngOnInit() {
    this.activeFilters = [];
    this.segmentFilterDefaults = [];

    // If we stashed filters for this key, retrieve and set them
    const stashedFilters = this.filter.retrieveStashedValuesForKey('team');
    if (stashedFilters) {
      this.activeFilters = stashedFilters;
      this.segmentFilterDefaults = this.activeFilters;
    }

    this.teamService.getPossibleRoles().subscribe((res) => {
      this.possibleRoles = res;

      this.searchCriteria = this.route.snapshot.paramMap.get('query');

      this.getTeam(this.activeFilters, this.currentOffset, this.searchCriteria);
    });
  }

  getTeam(filters: Filter[], page: number, search: string) {
    this.loading = true;
    this.teamService.getTeamMembers(filters, page, search, 20, true).subscribe((res: any) => {
      const data = res.data;
      res.data.forEach((member, index, arr) => {
        arr[index].services_labels = [];

        member.services.forEach((service) => {
          arr[index].services_labels.push(this.possibleRoles.find((role) => role.id === service).name);
        });
      });
      this.team = data;
      this.paginationData = res.meta?.pagination;
      this.loading = false;
    });
  }

  openNewTeam() {
    this.router.navigate([`/operations/team/create`]);
  }

  public handleFilterChanged(filters: Filter[]) {
    this.activeFilters = filters;

    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('team', this.activeFilters);

    // Retrieve conversations based on the new filters
    this.getTeam(this.activeFilters, 0, this.searchCriteria);
  }

  filterReset() {
    this.activeFilters = [];
    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('team', this.activeFilters);

    this.filterComponent.selfReset();
    this.ngOnInit();
  }

  public searchUpdated(searchVal: string) {
    // This is already debounced by the child presentation component
    const url = this.router.createUrlTree([{ query: searchVal }], { relativeTo: this.route }).toString();

    this.location.go(url);

    this.searchCriteria = searchVal;

    this.getTeam(this.activeFilters, 0, this.searchCriteria);
  }

  public handlePageChanged(newPageNo: number) {
    this.currentOffset = newPageNo;

    // We use offsets as the API deals in offsets rather than pages, when requesting data
    const newOffset = newPageNo * this.paginationData.per_page - this.paginationData.per_page;

    // Commit the new offset to the URL - our source of truth for offsets
    const url = this.router.createUrlTree([{ offset: newOffset }], { relativeTo: this.route }).toString();

    this.location.go(url);

    this.getTeam(this.activeFilters, newOffset, this.searchCriteria);
  }

  deletePrompt(member: any) {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: 'Team member',
        identifier: member.name,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteTeam(member);
      }
    });
  }

  deleteTeam(member: any) {
    this.teamService.deleteTeamMember(member.uuid).subscribe((res) => {
      if (res.error) {
        alert('There was an error deleting this team member');
      } else {
        const index = this.team.findIndex((team) => team.uuid === member.uuid);

        if (index !== -1) {
          this.team.splice(index, 1);
        }
      }
    });
  }
}
